/**
 * Contagem animada de números já formatados em pt-BR ("R$ 12.345,67", "87,5%", "1.204", "12 abertos"). O texto é
 * dividido em prefixo, número e sufixo; durante a animação só o número muda, com as mesmas casas decimais e
 * separadores. Textos que não são uma quantidade (datas, horas, frações como "3 de 5", códigos, anos) ficam como estão.
 */
export interface ParsedNumber {
  prefix: string;
  value: number;
  decimals: number;
  grouped: boolean;
  suffix: string;
}

const RE = /^([^\d]*?)(\d{1,3}(?:\.\d{3})+|\d+)(?:,(\d+))?([^\d]*)$/;

export function parseFormatted(text: string): ParsedNumber | null {
  const m = RE.exec(text);
  if (!m) return null;
  const [, prefix = "", intPart = "", frac, suffix = ""] = m;
  const grouped = intPart.includes(".");
  const value = Number(`${intPart.replace(/\./g, "")}${frac ? `.${frac}` : ""}`);
  if (!Number.isFinite(value)) return null;
  // 4+ dígitos sem separador de milhar costuma ser ano ou código (2026, 4521): não é quantidade.
  if (!grouped && !frac && intPart.length >= 4) return null;
  return { prefix, value, decimals: frac ? frac.length : 0, grouped, suffix };
}

export function formatParsed(p: ParsedNumber, value: number): string {
  const n = new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: p.decimals,
    maximumFractionDigits: p.decimals,
    useGrouping: p.grouped,
  }).format(value);
  return `${p.prefix}${n}${p.suffix}`;
}

export const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

/** Movimento permitido: navegador com animação e sem pedido de movimento reduzido. */
export function motionAllowed(): boolean {
  if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") return false;
  if (typeof window.matchMedia !== "function") return false;
  return !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
